import React, { useEffect, useMemo, useRef, useState } from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../../redux/actions'; //Import your actions
import { useFuse } from '../../../components/useFuse';
import 'materialize-css';
import '../../../styles/report.scss';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';

function buildScaffold(test) {
  let breadcrumbs = test.breadcrumbs.split('.');
  let reportId = breadcrumbs.reverse().pop();

  return {
    id: reportId,
    timestamp: Math.floor(Date.now()/1000),
    ...breadcrumbs.reduce((val, key) => ({[key]: val}), {...test})
  };
}

const AddReportTest = (props) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const componentId = props.component?.id;
  const isLog = `${componentId}` === '-1';

  // Tests already on this component, keyed by the test field they came from,
  // so each row can show whether picking it would add, restore, or do nothing.
  const placedTests = useMemo(() => {
    if (isLog || componentId === undefined) return {};

    const tests = props.report?.components?.[componentId]?.tests || {};
    return Object.values(tests).reduce((acc, test) => {
      if (test.testId !== undefined) acc[`${test.testId}`] = test;
      return acc;
    }, {});
  }, [props.report, componentId, isLog]);

  const options = useMemo(() => (
    (props.tests || [])
      .map(test => ({
        id: test.id,
        label: test.test_name || 'Unnamed test',
        desc: test.unit_of_measure || '',
        type: test.type,
        test: test,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [props.tests]);

  const matches = useFuse(query, options);
  const visible = query.trim().length ? matches : options;

  const statusOf = (option) => {
    const placed = placedTests[`${option.id}`];
    if (!placed) return 'new';
    return placed.deleted ? 'restorable' : 'added';
  };

  const firstSelectable = (list) => {
    const index = list.findIndex(option => statusOf(option) !== 'added');
    return index === -1 ? 0 : index;
  };

  useEffect(() => setActiveIndex(firstSelectable(visible)), [query, visible.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    listRef.current
      ?.querySelector('.add-test-result.is-active')
      ?.scrollIntoView({block: 'nearest'});
  }, [activeIndex]);

  const addTest = (option) => {
    const status = statusOf(option);
    if (status === 'added') return;

    if (status === 'restorable') {
      const restored = {...placedTests[`${option.id}`]};
      delete restored.deleted;

      const persist = props.saveNode || ((node, storage, onComplete) =>
        props.updateReport(buildScaffold(node), storage, onComplete)
      );

      return persist(
        restored,
        {fridge: true, freezer: props.authToken},
        () => props.close()
      );
    }

    const nodeId = Date.now();
    const testNode = {
      ...option.test,
      id: nodeId,
      testId: option.id,
      result: null,
      iAm: 'test',
      breadcrumbs: props.saveNode ? undefined : (
        isLog
          ? `${props.report.id}.components.-1.nodes.${new Date().toLocaleString().split(',')[0]}.${nodeId}`
          : `${props.report.id}.components.${componentId}.tests.${nodeId}`
      )
    };

    const persist = props.saveNode || ((node, storage, onComplete) =>
      props.updateReport(buildScaffold(node), storage, onComplete)
    );

    persist(
      testNode,
      {fridge: true, freezer: props.authToken},
      () => props.close()
    );
  };

  // Steps to the next row that can actually be picked, so held arrow keys
  // don't stall on tests already placed on the component.
  const moveActive = (delta) => {
    if (!visible.length) return;

    let next = activeIndex;
    for (let i = 0; i < visible.length; i++) {
      next = (next + delta + visible.length) % visible.length;
      if (statusOf(visible[next]) !== 'added') break;
    }

    setActiveIndex(next);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        return moveActive(1);
      case 'ArrowUp':
        e.preventDefault();
        return moveActive(-1);
      case 'Enter':
        e.preventDefault();
        if (visible[activeIndex]) addTest(visible[activeIndex]);
        return;
      case 'Escape':
        e.preventDefault();
        return props.close();
      default:
        return;
    }
  };

  if (!props.component) return null;

  const componentName = isLog ? 'Service notebook' : props.component.name;

  return (
    <div
      className={`report-modal add-test-modal${props.embedded ? ' is-embedded' : ''}`}
      onClick={props.embedded ? undefined : () => props.close()}
    >
      <div className="add-test-card" onClick={(e) => e.stopPropagation()}>
        <div className="add-test-head">
          <div className="add-test-heading">
            <span className="add-test-title">Add test</span>
            {componentName ? <span className="add-test-subtitle">{componentName}</span> : null}
          </div>

          <button
            type="button"
            className="add-test-close"
            aria-label="Close"
            onClick={() => props.close()}
          >
            <CloseRoundedIcon />
          </button>
        </div>

        <div className="add-test-search">
          <SearchRoundedIcon className="add-test-search-icon" aria-hidden="true" />
          <input
            className="add-test-input browser-default"
            type="text"
            autoFocus
            placeholder="Search tests"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {query.length ? (
            <button
              type="button"
              className="add-test-clear"
              aria-label="Clear search"
              onClick={() => setQuery('')}
            >
              <CloseRoundedIcon />
            </button>
          ) : null}
        </div>

        <div className="add-test-results" ref={listRef} role="listbox">
          {visible.length === 0 ? (
            <div className="add-test-empty">No tests match that search.</div>
          ) : visible.map((option, index) => {
            const status = statusOf(option);

            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                disabled={status === 'added'}
                className={`add-test-result is-${status}${index === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => addTest(option)}
              >
                <span className="add-test-result-main">
                  <span className="add-test-result-name">{option.label}</span>
                  {option.desc ? (
                    <span className="add-test-result-desc">{option.desc}</span>
                  ) : null}
                </span>

                <span className="add-test-result-meta">
                  {option.type === 'checkbox' ? (
                    <span className="add-test-tag">Check</span>
                  ) : null}

                  {status === 'added' ? (
                    <span className="add-test-state">
                      <CheckRoundedIcon />
                      Added
                    </span>
                  ) : status === 'restorable' ? (
                    <span className="add-test-state is-restore">
                      <RestartAltRoundedIcon />
                      Restore
                    </span>
                  ) : (
                    <AddRoundedIcon className="add-test-result-add" />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div className="add-test-foot">
          <span className="add-test-count">
            {visible.length} {visible.length === 1 ? 'test' : 'tests'}
          </span>
          <span className="add-test-hint">↑↓ browse · ↵ add · esc close</span>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    report: state.report,
    tests: state.testFields
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AddReportTest);
